import { BadgeCheck, Coins, Dumbbell, Gem, Scale, Sparkles } from "lucide-react";
import GuideTemplate from "@/pages/GuideTemplate";

const CFG = {
  seo: {
    testid: "gold-guide",
    path: "/gold-karat-guide",
    image: "/assets/gold-guide-hero.jpg",
    title: "10K vs 14K vs 18K Gold — What the Stamp Really Means | Gold Time Jewelry",
    description:
      "A Texas family jeweler explains gold karats honestly: 10K vs 14K vs 18K purity, durability, color and price — and which karat is right for your lifestyle. Gold Time Jewelry, Katy, Copperas Cove & Killeen TX.",
  },
  hero: {
    eyebrow: "The Gold Karat Guide",
    title: "10K, 14K, 18K —",
    titleAccent: "what the stamp really means",
    intro:
      "That tiny stamp inside your ring is a purity grade, a durability rating and a price tag all at once. Here's what each karat actually gives you — and which one we'd put on your hand, based on how you live.",
    image: "/assets/gold-guide-hero.jpg",
    imageAlt: "Three gold rings in 10K, 14K and 18K yellow gold side by side showing karat color differences",
    ctas: [
      { to: "/catalog", label: "Browse solid gold" },
      { to: "/sell", label: "Get your gold valued" },
    ],
  },
  facts: {
    title: "Karat is purity,",
    titleAccent: "measured in 24ths",
    paragraphs: [
      "Pure gold is 24 karats — 24 parts gold out of 24. But pure gold is soft enough to dent with a fingernail, so jewelers blend it with stronger metals like copper, silver and zinc. The karat stamp tells you the blend: 14K means 14 parts gold out of 24 (58.5%), 10K means 41.7%, and 18K means 75%.",
      "Every gold piece we sell is karat-stamped — and if you're selling, we test and weigh right at the counter where you can watch every number.",
    ],
    items: [
      { icon: BadgeCheck, label: "Karat-stamped gold" },
      { icon: Coins, label: "Priced by weight & purity" },
      { icon: Scale, label: "Weighed in front of you" },
    ],
  },
  showcase: {
    title: "Same word 'gold',",
    titleAccent: "very different metal",
    paragraphs: [
      "A 10K chain and an 18K chain can look near-identical across a room — but one is nearly twice the gold of the other. That's why two pieces of the same weight can be priced far apart, and why we always quote purity alongside price.",
      "When you sell to us, purity is exactly what we pay on: we assay each piece, multiply by the day's live gold rate, and show you the math.",
    ],
    image: "/assets/gold-guide-bars.jpg",
    imageAlt: "Fine gold bars and coins showing pure gold's rich color",
    imageFirst: true,
  },
  table: {
    title: "The three karats,",
    titleAccent: "side by side",
    columns: ["10K Gold", "14K Gold", "18K Gold"],
    rows: [
      { label: "Pure gold", values: ["41.7%", "58.5%", "75%"] },
      { label: "Durability", values: ["Hardest — most scratch & bend resistant", "Strong — ideal everyday balance", "Softer — handle with a little care"] },
      { label: "Color", values: ["Lighter, paler yellow", "Warm classic gold", "Deep, rich, unmistakable"] },
      { label: "Price", values: ["Most affordable", "Middle — best value per gram of gold", "Premium"] },
      { label: "Best for", values: ["Active hands, workwear, budget gifts", "Engagement rings, chains, daily wear", "Special-occasion and heirloom pieces"] },
    ],
  },
  cards: {
    title: "Which karat",
    titleAccent: "fits your life",
    items: [
      {
        icon: Dumbbell,
        title: "10K — the workhorse",
        text: "The hardest, most durable gold alloy and the friendliest price. If your hands work hard — tools, gym, ranch — 10K shrugs off what would scar softer gold.",
      },
      {
        icon: Gem,
        title: "14K — the sweet spot",
        text: "Nearly 60% pure gold with real durability — the most popular karat in America for a reason. Our default recommendation for rings and chains worn every day.",
      },
      {
        icon: Sparkles,
        title: "18K — the rich one",
        text: "Three-quarters pure gold with a depth of color you can spot across the room. Softer, yes — but for pieces worn on occasions, nothing else looks like it.",
      },
    ],
  },
  honestTake: {
    title: "Our honest take",
    text: "For most people wearing a piece every single day, 14K is the answer — real gold richness with the strength to match a real life. Go 10K if the piece will take abuse or the budget matters more than the blend. Go 18K when the piece is meant to be treasured, not tested. And whatever anyone tells you elsewhere: there is no bad choice among them — only the wrong choice for how you wear it.",
    image: "/assets/gold-guide-lifestyle.jpg",
    imageAlt: "Hand wearing a solid gold band in warm natural light",
  },
  faqs: [
    {
      q: "Is 10K gold real gold?",
      a: "Yes — 10K is legally real gold in the US, at 41.7% purity. Anything stamped 10K, 14K or 18K is a genuine gold alloy; the difference is how much pure gold is in the mix, not whether it's 'real'.",
    },
    {
      q: "Will 14K gold tarnish or fade?",
      a: "Solid gold does not tarnish or fade — the color is the metal all the way through. It can pick up surface dirt and fine scratches, which a quick professional polish removes. (Gold-plated and gold-filled pieces are different — the gold layer there can wear.)",
    },
    {
      q: "Which karat is best for an engagement ring?",
      a: "14K is the most popular choice — durable enough for decades of daily wear, rich enough in color to honor the occasion. Choose 18K if you want maximum gold richness and the ring won't see heavy work; choose 10K if budget or an active lifestyle leads.",
    },
    {
      q: "Does higher karat gold scratch more easily?",
      a: "Yes. More pure gold means a softer metal — an 18K ring will pick up marks faster than the same ring in 10K. Scratches don't remove meaningful gold, though, and periodic polishing keeps any karat looking new.",
    },
    {
      q: "Can I sell gold of any karat?",
      a: "Absolutely — we buy 10K, 14K, 18K and beyond, plus dental gold and broken pieces. Purity just sets the price per gram: we test each piece, weigh it in front of you, and pay on the day's live gold rate.",
    },
    {
      q: "Do white and rose gold follow the same karat system?",
      a: "Yes — 14K white gold and 14K yellow gold are both 58.5% pure gold. The color comes from the alloy metals (palladium or nickel for white, copper for rose), not the purity. Rhodium plating gives white gold its bright finish and can be refreshed inexpensively.",
    },
  ],
  cta: {
    heading: "Have gold to sell — or a karat to choose?",
    text: "Bring pieces in any condition for a free, no-pressure valuation at the counter — or browse solid gold online and pick up at any of our three Texas showrooms.",
    bgImage: "/assets/gold-guide-cta.jpg",
    buttons: [
      { to: "/sell", label: "Get a free valuation" },
      { to: "/catalog", label: "Shop solid gold" },
    ],
  },
  moreGuides: [
    { to: "/lab-grown-diamonds", label: "Lab-Grown Diamonds, Explained Honestly" },
    { to: "/diamond-buying-guide", label: "How to Buy a Diamond Without Overpaying" },
  ],
};

export default function GoldKaratGuidePage() {
  return <GuideTemplate cfg={CFG} />;
}
